import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { PlusIcon, XIcon, TrashIcon, ChevronIcon } from "../icons";
import { fmtMonth } from "../utils/helpers";
import UndoToast from "./UndoToast";
import S from "../styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

const STATUSES = ["New Ticket", "Production", "Resolved"];
const STATUS_COLORS = {
  "New Ticket": { bg: "rgba(200,149,108,0.1)", border: "#0033FF", text: "#0033FF" },
  Production: { bg: "rgba(59,130,246,0.1)", border: "#3b82f6", text: "#3b82f6" },
  Resolved: { bg: "rgba(34,197,94,0.1)", border: "#22c55e", text: "#22c55e" },
};

const PRIORITIES = ["Low", "Medium", "High", "Urgent"];
const PRIORITY_COLORS = {
  Low: "rgba(10,10,15,0.58)", Medium: "#0033FF", High: "#f97316", Urgent: "#ef4444",
};

const CATEGORIES = ["Order Issue", "Shipping", "Return / Refund", "Product Question", "Damaged Item", "Missing Item", "Custom Order", "Other"];

function EditTicketPopup({ ticket, store, employees, platforms, onClose, onSave }) {
  const [form, setForm] = useState({
    customerName: ticket.customerName || "",
    orderNumber: ticket.orderNumber || "",
    category: ticket.category || "Order Issue",
    platform: ticket.platform || "Etsy",
    subject: ticket.subject || "",
    notes: ticket.notes || "",
    priority: ticket.priority || "Medium",
    assignedTo: ticket.assignedTo || "",
    status: ticket.status || "New Ticket",
  });

  const save = () => {
    fetch(`${API_URL}/api/support-tickets/${store}/${ticket.id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    })
      .then((r) => r.json())
      .then((updated) => { onSave({ ...ticket, ...form, ...updated }); onClose(); })
      .catch(() => { onSave({ ...ticket, ...form }); onClose(); });
  };

  const fieldLabel = { fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, color: "rgba(10,10,15,0.58)", display: "block", marginBottom: 4 };

  return ReactDOM.createPortal(
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={onClose} style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)" }} />
      <div style={{ position: "relative", width: "100%", maxWidth: 560, background: "#F4F2EC", border: "2px solid #0A0A0F", borderRadius: 0, overflow: "hidden", zIndex: 301, maxHeight: "85vh", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "16px 20px", background: "#FFFFFF", borderBottom: "2px solid #0A0A0F", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: "#0A0A0F" }}>Edit Ticket</span>
          <button onClick={onClose} style={{ background: "none", border: "2px solid #0A0A0F", borderRadius: 0, padding: "4px 8px", cursor: "pointer", color: "rgba(10,10,15,0.58)", display: "flex", alignItems: "center" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <div style={{ padding: 20, overflowY: "auto", flex: 1 }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
            <div style={{ flex: 2, minWidth: 180 }}>
              <label style={fieldLabel}>Customer Name</label>
              <input value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })} style={{ ...S.input, marginBottom: 0, fontSize: 14 }} />
            </div>
            <div style={{ flex: 1, minWidth: 120 }}>
              <label style={fieldLabel}>Order #</label>
              <input value={form.orderNumber} onChange={(e) => setForm({ ...form, orderNumber: e.target.value })} style={{ ...S.input, marginBottom: 0, fontSize: 14, fontFamily: "'JetBrains Mono', monospace" }} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={fieldLabel}>Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} style={{ ...S.input, marginBottom: 0, fontSize: 14, cursor: "pointer" }}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 120 }}>
              <label style={fieldLabel}>Platform</label>
              <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} style={{ ...S.input, marginBottom: 0, fontSize: 14, cursor: "pointer" }}>
                {platforms.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 120 }}>
              <label style={fieldLabel}>Priority</label>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} style={{ ...S.input, marginBottom: 0, fontSize: 14, cursor: "pointer", color: PRIORITY_COLORS[form.priority] }}>
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={fieldLabel}>Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} style={{ ...S.input, marginBottom: 0, fontSize: 14, cursor: "pointer" }}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={fieldLabel}>Assigned To</label>
              <select value={form.assignedTo} onChange={(e) => setForm({ ...form, assignedTo: e.target.value })} style={{ ...S.input, marginBottom: 0, fontSize: 14, cursor: "pointer" }}>
                <option value="">Unassigned</option>
                {(employees || []).map((emp) => <option key={emp.id} value={emp.name}>{emp.name}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={fieldLabel}>Subject</label>
            <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} style={{ ...S.input, marginBottom: 0, fontSize: 14 }} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={fieldLabel}>Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={4} style={{ ...S.input, marginBottom: 0, fontSize: 14, resize: "vertical", fontFamily: "'Archivo', sans-serif" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 0, border: "2px solid #0A0A0F", background: "transparent", color: "rgba(10,10,15,0.58)", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "'Archivo', sans-serif" }}>Cancel</button>
            <button onClick={save} style={{ padding: "10px 24px", borderRadius: 0, border: "none", background: "#0033FF", color: "#FFFFFF", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "'Archivo', sans-serif" }}>Save Changes</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function TicketCard({ item, onDelete, onDragStart, onClick }) {
  const pc = PRIORITY_COLORS[item.priority] || "rgba(10,10,15,0.58)";
  return (
    <div
      draggable
      onDragStart={(e) => { e.dataTransfer.setData("text/plain", item.id); onDragStart(item.id); }}
      onClick={() => onClick && onClick(item)}
      style={{
        background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0,
        padding: "12px 14px", cursor: "pointer", transition: "all .15s", marginBottom: 8,
        borderLeft: `3px solid ${pc}`,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "rgba(10,10,15,0.58)")}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#0A0A0F")}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#0A0A0F" }}>{item.customerName}</span>
            {item.priority && item.priority !== "Low" && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: 0, background: `${pc}20`, color: pc, border: `1px solid ${pc}30`, textTransform: "uppercase", letterSpacing: 0.5 }}>{item.priority}</span>
            )}
          </div>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 4 }}>
            {item.platform && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 0, background: "rgba(139,97,65,0.15)", color: "#0033FF", border: "1px solid rgba(139,97,65,0.3)", textTransform: "uppercase", letterSpacing: 0.5 }}>{item.platform}</span>
            )}
            {item.category && (
              <span style={{ fontSize: 11, fontWeight: 600, padding: "1px 6px", borderRadius: 0, background: "rgba(90,138,181,0.1)", color: "rgba(10,10,15,0.58)", border: "1px solid rgba(90,138,181,0.2)" }}>{item.category}</span>
            )}
          </div>
          {item.orderNumber && (
            <div style={{ fontSize: 12, color: "rgba(10,10,15,0.58)", fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>#{item.orderNumber}</div>
          )}
          <div style={{ fontSize: 13, color: "#0A0A0F", marginTop: 4, lineHeight: 1.4 }}>{item.subject}</div>
          {item.notes && (
            <div style={{ fontSize: 12, color: "rgba(10,10,15,0.58)", marginTop: 4, lineHeight: 1.4 }}>{item.notes}</div>
          )}
        </div>
        <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); onDelete(item.id); }} style={{ ...S.iconBtn, padding: 4, flexShrink: 0 }}><TrashIcon /></button>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
        {item.assignedTo && (
          <span style={{ fontSize: 11, color: "#0033FF", fontWeight: 600 }}>{item.assignedTo}</span>
        )}
        <span style={{ fontSize: 11, color: "rgba(10,10,15,0.58)", fontFamily: "'JetBrains Mono', monospace", marginLeft: "auto" }}>{item.date}</span>
      </div>
    </div>
  );
}

function KanbanColumn({ status, items, onDrop, onDelete, onDragStart, onClickTicket }) {
  const [dragOver, setDragOver] = useState(false);
  const colors = STATUS_COLORS[status];

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); const id = e.dataTransfer.getData("text/plain"); if (id) onDrop(id, status); }}
      style={{
        flex: 1, minWidth: 220,
        background: dragOver ? "rgba(200,149,108,0.04)" : "#FFFFFF",
        border: `1px solid ${dragOver ? "#0033FF" : "#0A0A0F"}`,
        borderRadius: 0, padding: 12, transition: "all .2s",
        display: "flex", flexDirection: "column",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, padding: "0 4px" }}>
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: colors.border }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: colors.text, textTransform: "uppercase", letterSpacing: 0.8 }}>{status}</span>
        <span style={{
          fontSize: 12, fontFamily: "'JetBrains Mono', monospace",
          background: colors.bg, color: colors.text, border: `1px solid ${colors.border}30`,
          borderRadius: 0, padding: "1px 8px", marginLeft: "auto",
        }}>{items.length}</span>
      </div>
      <div style={{ flex: 1, minHeight: 80 }}>
        {items.map((item) => (
          <TicketCard key={item.id} item={item} onDelete={onDelete} onDragStart={onDragStart} onClick={onClickTicket} />
        ))}
        {items.length === 0 && (
          <div style={{ textAlign: "center", padding: 20, color: "rgba(10,10,15,0.58)", fontSize: 13, border: "1px dashed 0A0A0F", borderRadius: 0 }}>Drop here</div>
        )}
      </div>
    </div>
  );
}

const PLATFORMS_BASE = ["Etsy", "Amazon", "TikTok Shop", "eBay"];
const PLATFORMS_VV = [...PLATFORMS_BASE, "Reverb"];

export default function StoreSupportPage({ storeName, store, tickets, setTickets, archivedTickets, setArchivedTickets, employees }) {
  const isVV = store === "vibratovault";
  const platforms = isVV ? PLATFORMS_VV : PLATFORMS_BASE;
  const [adding, setAdding] = useState(false);
  const [newTicket, setNewTicket] = useState({ customerName: "", orderNumber: "", category: "Order Issue", platform: "Etsy", subject: "", notes: "", priority: "Medium", assignedTo: "" });
  const nameRef = useRef(null);

  const [pendingDelete, setPendingDelete] = useState(null); // { id, source: "active"|"archived", ticket }
  const [editingTicket, setEditingTicket] = useState(null);

  let userName = "";
  try { const u = JSON.parse(sessionStorage.getItem("nycmfg-user")); userName = u?.name || ""; } catch {}

  // Fetch tickets from API
  const fetchTickets = useCallback(() => {
    fetch(`${API_URL}/api/support-tickets?store=${store}`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const active = data.filter((t) => t.status !== "Resolved" || !t.resolvedDate);
          const archived = data.filter((t) => t.status === "Resolved" && t.resolvedDate);
          setTickets(active);
          setArchivedTickets(archived);
        }
      })
      .catch(() => {});
  }, [store, setTickets, setArchivedTickets]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const addTicket = () => {
    if (!newTicket.customerName.trim() || !newTicket.subject.trim()) return;
    const ticket = {
      store,
      customerName: newTicket.customerName.trim(),
      orderNumber: newTicket.orderNumber.trim(),
      category: newTicket.category,
      platform: newTicket.platform,
      subject: newTicket.subject.trim(),
      notes: newTicket.notes.trim(),
      priority: newTicket.priority,
      assignedTo: newTicket.assignedTo || userName,
      status: "New Ticket",
      createdBy: userName,
    };
    fetch(`${API_URL}/api/support-tickets`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(ticket),
    })
      .then((r) => r.json())
      .then((created) => { if (created.id) setTickets([created, ...tickets]); })
      .catch(() => {});
    setNewTicket({ customerName: "", orderNumber: "", category: "Order Issue", platform: "Etsy", subject: "", notes: "", priority: "Medium", assignedTo: "" });
    setAdding(false);
  };

  const deleteTicket = (id) => {
    const ticket = tickets.find((t) => t.id === id);
    setTickets(tickets.filter((t) => t.id !== id));
    setPendingDelete({ id, source: "active", ticket });
  };
  const deleteArchived = (id) => {
    const ticket = archivedTickets.find((t) => t.id === id);
    setArchivedTickets(archivedTickets.filter((t) => t.id !== id));
    setPendingDelete({ id, source: "archived", ticket });
  };
  const confirmDelete = () => {
    if (pendingDelete) {
      fetch(`${API_URL}/api/support-tickets/${store}/${pendingDelete.id}`, { method: "DELETE" }).catch(() => {});
      setPendingDelete(null);
    }
  };
  const undoDelete = () => {
    if (pendingDelete) {
      if (pendingDelete.source === "active") setTickets([...tickets, pendingDelete.ticket]);
      else setArchivedTickets([...archivedTickets, pendingDelete.ticket]);
      setPendingDelete(null);
    }
  };
  const moveTicket = (id, newStatus) => {
    if (newStatus === "Resolved") {
      // Archive any currently resolved tickets
      const currentlyResolved = tickets.filter((t) => t.status === "Resolved" && t.id !== id);
      const resolvedDate = new Date().toISOString().slice(0, 10);
      if (currentlyResolved.length > 0) {
        currentlyResolved.forEach((t) => {
          fetch(`${API_URL}/api/support-tickets/${store}/${t.id}`, {
            method: "PUT", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ status: "Resolved", resolvedDate }),
          }).catch(() => {});
        });
        const archived = currentlyResolved.map((t) => ({ ...t, resolvedDate }));
        setArchivedTickets([...archivedTickets, ...archived]);
        setTickets(tickets.filter((t) => t.status !== "Resolved" || t.id === id).map((t) => (t.id === id ? { ...t, status: "Resolved" } : t)));
      } else {
        setTickets(tickets.map((t) => (t.id === id ? { ...t, status: "Resolved" } : t)));
      }
      fetch(`${API_URL}/api/support-tickets/${store}/${id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "Resolved" }),
      }).catch(() => {});
    } else {
      setTickets(tickets.map((t) => (t.id === id ? { ...t, status: newStatus } : t)));
      fetch(`${API_URL}/api/support-tickets/${store}/${id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      }).catch(() => {});
    }
  };

  const onSaveEdit = (updated) => {
    setTickets(tickets.map((t) => (t.id === updated.id ? updated : t)));
  };

  const grouped = {};
  STATUSES.forEach((s) => (grouped[s] = []));
  tickets.forEach((t) => { const s = STATUSES.includes(t.status) ? t.status : "New Ticket"; grouped[s].push(t); });

  const openCount = tickets.filter((t) => t.status !== "Resolved").length;

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: "#0A0A0F", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1, marginBottom: 4 }}>{storeName} — Support Tickets</h2>
        <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 20 }}>Track and resolve customer support issues</p>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 24 }}>
        <div style={S.statCard}><span style={S.statLabel}>Total Tickets</span><span style={S.statValue}>{tickets.length}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Open</span><span style={{ ...S.statValue, color: openCount > 0 ? "#0033FF" : "#34d399" }}>{openCount}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>New</span><span style={{ ...S.statValue, color: "#0033FF" }}>{grouped["New Ticket"].length}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Production</span><span style={{ ...S.statValue, color: "#3b82f6" }}>{grouped["Production"].length}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Resolved</span><span style={{ ...S.statValue, color: "#22c55e" }}>{grouped["Resolved"].length}</span></div>
      </div>

      {/* New Ticket */}
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end" }}>
        <button onClick={() => { setAdding(!adding); setTimeout(() => nameRef.current?.focus(), 50); }} style={S.addBtn}>
          {adding ? <XIcon /> : <PlusIcon />} {adding ? "Cancel" : "New Ticket"}
        </button>
      </div>

      {adding && (
        <div style={{
          background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0,
          padding: 20, marginBottom: 24,
        }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
            <div style={{ flex: 2, minWidth: 180 }}>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, color: "rgba(10,10,15,0.58)", display: "block", marginBottom: 4 }}>Customer Name *</label>
              <input ref={nameRef} value={newTicket.customerName} onChange={(e) => setNewTicket({ ...newTicket, customerName: e.target.value })}
                placeholder="e.g. John Smith" style={{ ...S.input, marginBottom: 0, fontSize: 14 }} />
            </div>
            <div style={{ flex: 1, minWidth: 120 }}>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, color: "rgba(10,10,15,0.58)", display: "block", marginBottom: 4 }}>Order #</label>
              <input value={newTicket.orderNumber} onChange={(e) => setNewTicket({ ...newTicket, orderNumber: e.target.value })}
                placeholder="e.g. 3847291" style={{ ...S.input, marginBottom: 0, fontSize: 14, fontFamily: "'JetBrains Mono', monospace" }} />
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, color: "rgba(10,10,15,0.58)", display: "block", marginBottom: 4 }}>Category</label>
              <select value={newTicket.category} onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
                style={{ ...S.input, marginBottom: 0, fontSize: 14, cursor: "pointer" }}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 120 }}>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, color: "rgba(10,10,15,0.58)", display: "block", marginBottom: 4 }}>Priority</label>
              <select value={newTicket.priority} onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                style={{ ...S.input, marginBottom: 0, fontSize: 14, cursor: "pointer", color: PRIORITY_COLORS[newTicket.priority] }}>
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 120 }}>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, color: "rgba(10,10,15,0.58)", display: "block", marginBottom: 4 }}>Platform</label>
              <select value={newTicket.platform} onChange={(e) => setNewTicket({ ...newTicket, platform: e.target.value })}
                style={{ ...S.input, marginBottom: 0, fontSize: 14, cursor: "pointer" }}>
                {platforms.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
            <div style={{ flex: 3, minWidth: 250 }}>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, color: "rgba(10,10,15,0.58)", display: "block", marginBottom: 4 }}>Subject *</label>
              <input value={newTicket.subject} onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") addTicket(); }}
                placeholder="Brief description of the issue" style={{ ...S.input, marginBottom: 0, fontSize: 14 }} />
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, color: "rgba(10,10,15,0.58)", display: "block", marginBottom: 4 }}>Assigned To</label>
              <select value={newTicket.assignedTo} onChange={(e) => setNewTicket({ ...newTicket, assignedTo: e.target.value })}
                style={{ ...S.input, marginBottom: 0, fontSize: 14, cursor: "pointer" }}>
                <option value="">Select employee</option>
                {(employees || []).map((emp) => <option key={emp.id} value={emp.name}>{emp.name}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, color: "rgba(10,10,15,0.58)", display: "block", marginBottom: 4 }}>Notes</label>
            <textarea value={newTicket.notes} onChange={(e) => setNewTicket({ ...newTicket, notes: e.target.value })}
              placeholder="Additional details, customer contact info, etc."
              rows={3} style={{ ...S.input, marginBottom: 0, fontSize: 14, resize: "vertical", fontFamily: "'Archivo', sans-serif" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button onClick={addTicket} disabled={!newTicket.customerName.trim() || !newTicket.subject.trim()} style={{
              padding: "10px 24px", borderRadius: 0, border: "none", fontSize: 14, fontWeight: 600,
              fontFamily: "'Archivo', sans-serif", cursor: (newTicket.customerName.trim() && newTicket.subject.trim()) ? "pointer" : "default",
              background: (newTicket.customerName.trim() && newTicket.subject.trim()) ? "#0033FF" : "#0A0A0F",
              color: (newTicket.customerName.trim() && newTicket.subject.trim()) ? "#FFFFFF" : "rgba(10,10,15,0.58)",
            }}>Create Ticket</button>
          </div>
        </div>
      )}

      {/* Kanban Board */}
      <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 8, marginBottom: 32 }}>
        {STATUSES.map((status) => (
          <KanbanColumn
            key={status}
            status={status}
            items={grouped[status]}
            onDrop={moveTicket}
            onDelete={deleteTicket}
            onDragStart={() => {}}
            onClickTicket={setEditingTicket}
          />
        ))}
      </div>

      {/* Old Tickets — grouped by month */}
      <OldTickets archivedTickets={archivedTickets} onDelete={deleteArchived} />

      {pendingDelete && (
        <UndoToast
          message={`Deleted ticket: ${pendingDelete.ticket?.customerName || "Unknown"}`}
          onUndo={undoDelete}
          onExpire={confirmDelete}
          duration={5}
        />
      )}

      {editingTicket && (
        <EditTicketPopup
          ticket={editingTicket}
          store={store}
          employees={employees}
          platforms={platforms}
          onClose={() => setEditingTicket(null)}
          onSave={onSaveEdit}
        />
      )}
    </div>
  );
}

function OldTickets({ archivedTickets, onDelete }) {
  const [collapsed, setCollapsed] = useState({});
  const toggle = (k) => setCollapsed((c) => ({ ...c, [k]: !c[k] }));

  const monthGroups = useMemo(() => {
    const sorted = [...archivedTickets].sort((a, b) => (b.resolvedDate || b.date || "").localeCompare(a.resolvedDate || a.date || ""));
    const map = {};
    for (const t of sorted) {
      const d = t.resolvedDate || t.date || "";
      // Parse "Mar 19, 2026" or "2026-03-19" to get month key
      let mk = "undated";
      if (d.match(/^\d{4}-\d{2}/)) {
        mk = d.slice(0, 7);
      } else {
        try { const parsed = new Date(d); if (!isNaN(parsed)) mk = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}`; } catch {}
      }
      if (!map[mk]) map[mk] = [];
      map[mk].push(t);
    }
    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [archivedTickets]);

  if (archivedTickets.length === 0) return null;

  return (
    <div>
      <h3 style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: "rgba(10,10,15,0.58)", marginBottom: 12 }}>
        Old Tickets
        <span style={{ ...S.jobCountBadge, marginLeft: 8, background: "#22c55e20", color: "#22c55e" }}>{archivedTickets.length}</span>
      </h3>
      {monthGroups.map(([mk, tix]) => {
        const isOpen = collapsed[mk] !== true;
        return (
          <div key={mk} style={{ marginBottom: 12 }}>
            <button onClick={() => toggle(mk)} style={{ ...S.monthHeader, borderColor: "#0A0A0F" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <ChevronIcon open={isOpen} />
                <span style={{ fontSize: 17, fontWeight: 600, color: "#0A0A0F" }}>{mk === "undated" ? "Undated" : fmtMonth(mk)}</span>
                <span style={S.jobCountBadge}>{tix.length} ticket{tix.length !== 1 ? "s" : ""}</span>
              </div>
            </button>
            {isOpen && (
              <div style={{ ...S.tableWrap, borderTop: "none", borderTopLeftRadius: 0, borderTopRightRadius: 0, overflowX: "auto" }}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={{ ...S.th, width: "12%" }}>Date</th>
                      <th style={{ ...S.th, width: "16%" }}>Customer</th>
                      <th style={{ ...S.th, width: "9%" }}>Order #</th>
                      <th style={{ ...S.th, width: "9%" }}>Platform</th>
                      <th style={{ ...S.th, width: "11%" }}>Category</th>
                      <th style={{ ...S.th, width: "23%" }}>Subject</th>
                      <th style={{ ...S.th, width: "8%" }}>Priority</th>
                      <th style={{ ...S.th, width: "8%", textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tix.map((t) => {
                      const pc = PRIORITY_COLORS[t.priority] || "rgba(10,10,15,0.58)";
                      return (
                        <tr key={t.id}>
                          <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{t.date}</td>
                          <td style={{ ...S.td, fontWeight: 500, color: "#0A0A0F" }}>{t.customerName}</td>
                          <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{t.orderNumber || "—"}</td>
                          <td style={S.td}>
                            {t.platform ? (
                              <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 6px", borderRadius: 0, background: "rgba(139,97,65,0.15)", color: "#0033FF", border: "1px solid rgba(139,97,65,0.3)", textTransform: "uppercase", letterSpacing: 0.5 }}>{t.platform}</span>
                            ) : "—"}
                          </td>
                          <td style={S.td}>
                            {t.category ? (
                              <span style={{ fontSize: 12, fontWeight: 600, padding: "2px 6px", borderRadius: 0, background: "rgba(90,138,181,0.1)", color: "rgba(10,10,15,0.58)", border: "1px solid rgba(90,138,181,0.2)" }}>{t.category}</span>
                            ) : "—"}
                          </td>
                          <td style={{ ...S.td, color: "#0A0A0F", fontSize: 13 }}>{t.subject}</td>
                          <td style={S.td}>
                            <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 6px", borderRadius: 0, background: `${pc}20`, color: pc, border: `1px solid ${pc}30`, textTransform: "uppercase" }}>{t.priority || "Medium"}</span>
                          </td>
                          <td style={{ ...S.td, textAlign: "right" }}>
                            <button onClick={() => onDelete(t.id)} style={S.iconBtn} title="Delete"><TrashIcon /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
